'use memo'

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { TeamFinderFilters } from '@sportninja/common/features/teamFinder/types'
import {
  EMPTY_FILTERS,
  paramsToFilters,
} from '@sportninja/common/features/teamFinder/filterParams'

const emptyFilters = EMPTY_FILTERS

export interface UseTeamFinderFormResult {
  filters: TeamFinderFilters
  setGender: (id: string) => void
  /** `sportId` is the sport (or the PARENT generic id for a basic-stats pick);
   * `genericSportId` is the specific basic-stats id ('' for full-stats). */
  setSport: (sportId: string, genericSportId?: string) => void
  /** Replaces the selected positions (multi-select; empty = any). */
  setPositions: (ids: string[]) => void
  setCountry: (name?: string, countryId?: string | number) => void
  setProvince: (name?: string, provinceId?: string | number) => void
  setCity: (city: string) => void
  setTeam: (team: string) => void
  setOrganization: (organization: string) => void
  fieldErrors: Record<string, string>
  setFieldErrors: (errors: Record<string, string>) => void
  clearFieldError: (field: string) => void
  resetErrors: () => void
  /** Reset every filter back to empty and clear all inline errors. */
  clearFilters: () => void
}

export const useTeamFinderForm = (): UseTeamFinderFormResult => {
  'use memo'

  // Seeded from the URL so a shared link opens with its filters already in the
  // form. Read once via the initializer — after mount the form owns the state,
  // and re-reading would fight the user's own edits.
  const { search } = useLocation()
  const [filters, setFilters] = useState<TeamFinderFilters>(() =>
    paramsToFilters(search)
  )
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => {
      if (!prev[field]) {
        return prev
      }
      const next = { ...prev }
      delete next[field]
      return next
    })

  const setGender = (genderId: string) => {
    clearFieldError('genderId')
    setFilters((f) => ({ ...f, genderId }))
  }

  const setSport = (sportId: string, genericSportId = '') => {
    clearFieldError('sportId')
    clearFieldError('positionIds')
    /*
     * Positions are cleared, NOT defaulted, on a sport change.
     *
     * Single-select pre-selected the sport's default so the search never sent
     * a null position. Multi-select has no such need — empty means "any" — and
     * auto-adding one would silently re-introduce a position the user had just
     * removed. The previous sport's ids are meaningless here anyway: the same
     * numeric id is a different position in another sport.
     */
    setFilters((f) => ({ ...f, sportId, genericSportId, positionIds: [] }))
  }

  const setPositions = (positionIds: string[]) => {
    clearFieldError('positionIds')
    setFilters((f) => ({ ...f, positionIds }))
  }

  const setCountry = (country?: string, countryId?: string | number) => {
    clearFieldError('country')
    // Country change invalidates the previously chosen province + its id.
    setFilters((f) => ({
      ...f,
      country: country ?? '',
      countryId: country && countryId != null ? String(countryId) : '',
      province: '',
      provinceId: '',
    }))
  }

  const setProvince = (province?: string, provinceId?: string | number) => {
    clearFieldError('province')
    setFilters((f) => ({
      ...f,
      province: province ?? '',
      provinceId: provinceId != null ? String(provinceId) : '',
    }))
  }

  const setCity = (city: string) => {
    clearFieldError('city')
    setFilters((f) => ({ ...f, city }))
  }

  const setTeam = (team: string) => {
    clearFieldError('team')
    setFilters((f) => ({ ...f, team }))
  }

  const setOrganization = (organization: string) => {
    clearFieldError('organization')
    setFilters((f) => ({ ...f, organization }))
  }

  const resetErrors = () => setFieldErrors({})

  const clearFilters = () => {
    setFilters(emptyFilters)
    setFieldErrors({})
  }

  return {
    filters,
    setGender,
    setSport,
    setPositions,
    setCountry,
    setProvince,
    setCity,
    setTeam,
    setOrganization,
    fieldErrors,
    setFieldErrors,
    clearFieldError,
    resetErrors,
    clearFilters,
  }
}
